import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory, useLocation } from "react-router-dom";
import Loader from "react-loader-spinner";
import { FiArrowLeft, FiDownload, FiAlertCircle, FiEye } from "react-icons/fi";
import { API_URLS, SOCIAL_EVENT_TYPES, SOCIAL_MVP_LABELS } from '../constants/config';
import {
    fetchSocialPatterns,
    fetchPicturePreview,
    fetchPictureBase64,
    fetchAdminLeaguesWithSeasons,
    fetchLeagueStages,
    fetchStageMatches,
    fetchMatchesByDate,
    fetchLeaguePlayers,
    fetchLeagueGroups,
    createPreviewCancelSource,
} from '../actions/socialPicturesActions';
import {
    normalizePattern,
    secureAssetUrls,
    previewAspectRatio,
    buildFileName,
    downloadBase64Png,
    resolveImage,
    patternPostsNews,
    buildNewsData,
} from '../utilities/infographics';
import { CONTROL_REGISTRY, PENDING_CONTROL_LABELS } from '../components/infographics/controls';
import EntityPickerModal from '../components/infographics/EntityPickerModal';
import TourTeamEditor from '../components/infographics/TourTeamEditor';
import PlayoffEditor from '../components/infographics/PlayoffEditor';
import NewsRequestModal from '../components/infographics/NewsRequestModal';
import '../styles/editModal.css';
import '../styles/infographics.css';

// Редактор инфографики. Перенос CreateSocialPost из приложения.
// Живое превью просит у сервера HTML — это дёшево и переживает частые
// изменения параметров. Тяжёлый серверный рендер PNG вызывается только
// по кнопке «Точное превью» и при скачивании, причём готовый PNG
// переиспользуется, чтобы не рендерить дважды.

const PREVIEW_DEBOUNCE_MS = 300;

// HTML превью приходит в натуральном размере шаблона (cut_size_width/height)
// и не влезает в колонку — ужимаем его через transform, чтобы было видно целиком
const NATURAL_FALLBACK_WIDTH = 1080;
const NATURAL_FALLBACK_HEIGHT = 1350;

// allow-scripts без allow-same-origin: шаблоны могут использовать JS,
// но у iframe остаётся отдельный origin и до страницы админки он не дотянется
const PREVIEW_SANDBOX = 'allow-scripts';
const LAST_LEAGUE_KEY = 'infographics:lastLeague';

// Ключи состояния совпадают с приложением: сервер присылает required_settings
// именно этими именами, и проверка "чего не хватает" должна работать так же
const emptyParams = () => ({
    activeLeague: false,
    seasonIndex: 0,
    date: '',
    activeStage: false,
    activeMatch: false,
    activeMatches: false,
    activePlayer: false,
    activePlayers: false,
    activeGroups: false,
    activeScheme: false,
    activeMainTeam: false,
    activeSubTeam: false,
    activeCoaches: false,
    activePlayoffMatches: false,
    activeColors: false,
    playersTeamImages: false,
    customBg: false,
    customBgTwo: false,
    customBgThree: false,
    customBgFour: false,
    customBgFive: false,
    sponsorEnabled: false,
    videoEnabled: false,
    goalTimeEnabled: true,
    isGroupEvent: false,
    includeTourTeam: true,
    label: '',
    social_vk: '',
    social_inst: '',
    activeColor: 1,
    activeHeader: 1,
    activeBottom: 1,
    eventIndex: 0,
    mvpIndex: 0,
});

// В состоянии группы держим объектами — так контрол показывает название.
// Сервер же ждёт массив названий, и всегда массив, даже когда группа одна
// (в приложении так делает LeagueGroupChoose.setGroups). Объект он не разбирает
// и вместо ошибки уходит в бесконечный запрос.
const toGroupNames = (value) => {
    if (!value) return value;
    const list = Array.isArray(value) ? value : [value];
    const names = list
        .map(one => (typeof one === 'string' ? one : (one && one.name)))
        .filter(Boolean);
    return names.length ? names : false;
};

// Какой контрол за какой ключ состояния отвечает — нужно, чтобы подсказать
// незаполненные параметры, даже если сервер не пометил их обязательными
const CONTROL_STATE_KEYS = {
    renderChooseLeague: 'activeLeague',
    renderChooseStage: 'activeStage',
    renderChooseDate: 'date',
    renderMatchChoose: 'activeMatch',
    renderMatchesChoose: 'activeMatches',
    renderPlayerChoose: 'activePlayer',
    renderLeagueChoose: 'activeGroups',
    renderLeagueChooseMultiple: 'activeGroups',
    renderTeamChoose: 'activePlayers',
    renderSchemeChoose: 'activeScheme',
    renderPlayoffChoose: 'activePlayoffMatches',
    renderMainTeamChoose: 'activeMainTeam',
    renderSubTeamChoose: 'activeSubTeam',
    renderCoachesChoose: 'activeCoaches',
};

// Сервер может молча зависнуть, если чего-то не хватает, — на таймауте
// подсказываем, что именно осталось незаполненным
const describeError = (error, emptyControls) => {
    const isTimeout = error && (error.code === 'ECONNABORTED'
        || (error.message || '').indexOf('timeout') !== -1);
    if (isTimeout) {
        const tail = emptyControls.length
            ? ` Возможная причина: не заполнено — ${emptyControls.join(', ')}.`
            : '';
        return `Сервер не ответил вовремя и картинку не прислал.${tail}`;
    }
    return (error.response && error.response.data)
        || error.message
        || 'Не удалось получить картинку';
};

// Человекочитаемые названия для подсказки "чего не хватает".
// В приложении кнопка сохранения просто не появлялась и причина была не видна.
const REQUIRED_LABELS = {
    activeLeague: 'лигу',
    activeStage: 'тур',
    activeMatch: 'матч',
    activeMatches: 'матчи',
    activePlayer: 'игрока',
    activePlayers: 'состав',
    activeGroups: 'группу',
    activeScheme: 'схему',
    activeMainTeam: 'состав команды',
    activeSubTeam: 'состав второй команды',
    activeCoaches: 'тренеров',
    activePlayoffMatches: 'матчи плей-офф',
    date: 'дату',
};

export default function InfographicsEditor() {
    const { patternId } = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    // client_id нужен руту матчей на дату — без него он отвечает 500
    const userState = useSelector(state => state.user);
    const clientId = userState && userState.userData && userState.userData.data
        ? userState.userData.data.client_id
        : null;

    const seeded = location.state && location.state.pattern ? location.state.pattern : null;
    const [pattern, setPattern] = useState(seeded ? normalizePattern(seeded) : null);
    const [patternLoading, setPatternLoading] = useState(!seeded);

    const [params, setParams] = useState(emptyParams);
    const [preview, setPreview] = useState({ html: null, base64: null });
    const [previewLoading, setPreviewLoading] = useState(false);
    // Показан ли сейчас точный PNG с сервера (а не быстрое HTML-превью).
    // Сбрасывается при любом изменении параметров
    const [exact, setExact] = useState(false);
    const [error, setError] = useState(null);

    const [leagues, setLeagues] = useState([]);
    const [leaguePickerOpen, setLeaguePickerOpen] = useState(false);
    const [stages, setStages] = useState([]);
    const [stagePickerOpen, setStagePickerOpen] = useState(false);

    const [downloading, setDownloading] = useState(false);
    // Какой пикер открыт: match | matches | player | group | groups |
    // mainTeam | subTeam | coaches
    const [picker, setPicker] = useState(null);
    const [tourTeamOpen, setTourTeamOpen] = useState(false);
    const [playoffOpen, setPlayoffOpen] = useState(false);
    // Слотовые редакторы просят выбрать игрока или матч «во внешнем» пикере,
    // поэтому держим отложенный обработчик выбора
    const [slotRequest, setSlotRequest] = useState(null);
    // Для шаблонов 1, 4, 6, 9 перед скачиванием спрашиваем текст новости
    const [newsOpen, setNewsOpen] = useState(false);

    const cancelRef = useRef(null);
    const debounceRef = useRef(null);
    const requestSeqRef = useRef(0);
    const previewRef = useRef(null);
    const [previewScale, setPreviewScale] = useState(1);

    // ---- Дескриптор шаблона ----
    // При переходе из каталога он уже в location.state; при перезагрузке
    // страницы по прямой ссылке — дочитываем каталог
    useEffect(() => {
        if (pattern) return;
        let cancelled = false;
        setPatternLoading(true);
        dispatch(fetchSocialPatterns(0, 0, 500))
            .then(data => {
                if (cancelled) return;
                const rows = (Array.isArray(data) ? data : []).map(normalizePattern);
                const found = rows.find(item => String(item.id) === String(patternId));
                setPattern(found || null);
            })
            .catch(e => { if (!cancelled) setError(e); })
            .finally(() => { if (!cancelled) setPatternLoading(false); });
        return () => { cancelled = true; };
    }, [dispatch, pattern, patternId]);

    // ---- Справочник лиг ----
    useEffect(() => {
        let cancelled = false;
        dispatch(fetchAdminLeaguesWithSeasons())
            .then(data => {
                if (cancelled) return;
                const rows = (Array.isArray(data) ? data : (data && data.data) || [])
                    .filter(item => Number(item.league_id) > 0);
                setLeagues(rows);
                // Возвращаем последнюю лигу админа — обычно постят по одной и той же
                try {
                    const savedId = localStorage.getItem(LAST_LEAGUE_KEY);
                    if (savedId) {
                        const saved = rows.find(item => String(item.league_id) === savedId);
                        if (saved) {
                            setParams(prev => ({ ...prev, activeLeague: saved, seasonIndex: 0 }));
                        }
                    }
                } catch (e) {
                    // localStorage может быть недоступен — не критично
                }
            })
            .catch(e => { if (!cancelled) console.error('Ошибка загрузки лиг:', e); });
        return () => { cancelled = true; };
    }, [dispatch]);

    const activeSeason = useMemo(() => {
        const league = params.activeLeague;
        if (!league || !league.seasons || !league.seasons.length) return null;
        return league.seasons[params.seasonIndex] || league.seasons[0];
    }, [params.activeLeague, params.seasonIndex]);

    // ---- Туры выбранной лиги и сезона ----
    useEffect(() => {
        const league = params.activeLeague;
        if (!league || !activeSeason) {
            setStages([]);
            return undefined;
        }
        let cancelled = false;
        dispatch(fetchLeagueStages(league.league_id, activeSeason.season_id))
            .then(data => {
                if (cancelled) return;
                const rows = Array.isArray(data) ? data : (data && data.data) || [];
                setStages(rows);
            })
            .catch(e => { if (!cancelled) console.error('Ошибка загрузки туров:', e); });
        return () => { cancelled = true; };
    }, [dispatch, params.activeLeague, activeSeason]);

    // ---- Генерация превью ----
    const requestUrl = useMemo(() => {
        if (!pattern) return null;
        if (pattern.route && pattern.route.length) return pattern.route;
        return API_URLS[pattern.url] || pattern.url;
    }, [pattern]);

    const buildRequestParams = useCallback((extra) => ({
        league_id: params.activeLeague ? params.activeLeague.league_id : undefined,
        season_id: activeSeason ? activeSeason.season_id : undefined,
        date: params.date,
        partnersEnable: params.sponsorEnabled,
        vk: params.social_vk,
        inst: params.social_inst,
        player_id: params.activePlayer ? params.activePlayer.id : undefined,
        team_id: params.activePlayer ? params.activePlayer.team_id : undefined,
        stage_id: params.activeStage ? params.activeStage.id : undefined,
        match_id: params.activeMatch ? params.activeMatch.id : undefined,
        groups: toGroupNames(params.activeGroups),
        event_type: SOCIAL_EVENT_TYPES[params.eventIndex]
            ? SOCIAL_EVENT_TYPES[params.eventIndex].value : undefined,
        players: params.activePlayers,
        scheme: params.activeScheme,
        playersTeamImages: params.playersTeamImages,
        activeColor: params.activeColor,
        activeHeader: params.activeHeader,
        activeBottom: params.activeBottom,
        activeMatches: params.activeMatches,
        goalTimeEnabled: params.goalTimeEnabled,
        isGroupEvent: params.isGroupEvent,
        activeMainTeam: params.activeMainTeam,
        activeSubTeam: params.activeSubTeam,
        activeCoaches: params.activeCoaches,
        label: params.label,
        videoEnabled: params.videoEnabled,
        activeColors: params.activeColors,
        customBg: params.customBg,
        customBgTwo: params.customBgTwo,
        customBgThree: params.customBgThree,
        customBgFour: params.customBgFour,
        customBgFive: params.customBgFive,
        mvpLabel_type: SOCIAL_MVP_LABELS[params.mvpIndex]
            ? SOCIAL_MVP_LABELS[params.mvpIndex].label : undefined,
        activePlayoffMatches: params.activePlayoffMatches,
        includeTourTeam: params.includeTourTeam,
        ...(extra || {}),
    }), [params, activeSeason]);

    useEffect(() => {
        if (!pattern || !requestUrl) return undefined;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (cancelRef.current) cancelRef.current.cancel('replaced');
            const source = createPreviewCancelSource();
            cancelRef.current = source;
            // Порядковый номер запроса: отвечать за индикатор загрузки должен
            // только самый свежий, иначе устаревший ответ снимет спиннер с нового
            const seq = requestSeqRef.current + 1;
            requestSeqRef.current = seq;
            setPreviewLoading(true);
            setError(null);
            dispatch(fetchPicturePreview(requestUrl, buildRequestParams(), source))
                .then(data => {
                    if (seq !== requestSeqRef.current) return;
                    // null — запрос отменили, но более свежего нет:
                    // спиннер снимаем, иначе он останется навсегда
                    if (data === null) {
                        setPreviewLoading(false);
                        return;
                    }
                    if (data && data.html) {
                        setPreview({ html: secureAssetUrls(data.html), base64: null });
                    } else {
                        setPreview({ html: null, base64: data });
                    }
                    // Параметры изменились — точный PNG больше не актуален
                    setExact(false);
                    setPreviewLoading(false);
                })
                .catch(e => {
                    if (seq !== requestSeqRef.current) return;
                    console.error('Ошибка генерации превью:', e);
                    setError(e);
                    setPreviewLoading(false);
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [dispatch, pattern, requestUrl, buildRequestParams]);

    useEffect(() => () => {
        if (cancelRef.current) cancelRef.current.cancel('unmount');
    }, []);

    // Масштаб превью пересчитываем под ширину колонки и следим за ресайзом окна
    const naturalPreviewWidth = pattern && Number(pattern.cut_size_width) > 0
        ? Number(pattern.cut_size_width) : NATURAL_FALLBACK_WIDTH;

    useEffect(() => {
        const node = previewRef.current;
        if (!node) return undefined;
        const update = () => {
            const box = node.clientWidth;
            if (box > 0) setPreviewScale(box / naturalPreviewWidth);
        };
        update();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', update);
            return () => window.removeEventListener('resize', update);
        }
        const observer = new ResizeObserver(update);
        observer.observe(node);
        return () => observer.disconnect();
    }, [naturalPreviewWidth, preview.html]);

    // ---- Изменение параметра ----
    const setValue = useCallback((key, value) => {
        setParams(prev => ({ ...prev, [key]: value }));
    }, []);

    const pickLeague = (league) => {
        setParams(prev => ({
            ...prev,
            activeLeague: league,
            seasonIndex: 0,
            // Тур принадлежит сезону — при смене лиги он больше не валиден
            activeStage: false,
            activeMatch: false,
            activeMatches: false,
        }));
        setLeaguePickerOpen(false);
        try {
            localStorage.setItem(LAST_LEAGUE_KEY, String(league.league_id));
        } catch (e) {
            // localStorage может быть недоступен — не критично
        }
    };

    const controlContext = {
        openLeaguePicker: () => setLeaguePickerOpen(true),
        openStagePicker: () => setStagePickerOpen(true),
        openPicker: (kind) => {
            if (kind === 'tourTeam') { setTourTeamOpen(true); return; }
            if (kind === 'playoff') { setPlayoffOpen(true); return; }
            setPicker(kind);
        },
        leagues,
        stages,
    };

    // ---- Загрузчики для модалки выбора ----
    const leagueId = params.activeLeague ? params.activeLeague.league_id : null;
    const seasonId = activeSeason ? activeSeason.season_id : null;

    // Матчи берём по туру, а если тур не выбран — по дате: так же, как решает
    // приложение (CreateSocialPost.renderMatchesChoose)
    const loadMatches = () => {
        if (params.activeStage) {
            return dispatch(fetchStageMatches(leagueId, seasonId, params.activeStage.id));
        }
        if (params.date) {
            return dispatch(fetchMatchesByDate(params.date, clientId));
        }
        return Promise.resolve([]);
    };

    const loadPlayers = (teamId) => dispatch(fetchLeaguePlayers(leagueId, seasonId, '', teamId));

    const homeTeamId = params.activeMatch ? params.activeMatch.home_team_id : undefined;
    const guestTeamId = params.activeMatch ? params.activeMatch.guest_team_id : undefined;

    const loadGroups = () => dispatch(fetchLeagueGroups(leagueId, seasonId, false));

    const matchView = (match) => ({
        key: String(match.id),
        title: `${match.home_team_name || 'Команда'} — ${match.guest_team_name || 'Команда'}`,
        subtitle: match.match_date || match.date || '',
        image: match.home_team_image,
    });

    const playerView = (player) => ({
        key: String(player.player_id || player.id),
        title: `${player.family || ''} ${player.name || ''}`.trim() || 'Игрок',
        subtitle: player.team_name || '',
        image: player.player_image || player.image,
    });

    const groupView = (group) => ({
        key: String(group.id !== undefined ? group.id : group.name),
        title: `Группа ${group.name}`,
    });

    const PICKERS = {
        match: {
            title: 'Выберите матч',
            load: loadMatches,
            view: matchView,
            multiple: false,
            onPick: (item) => setValue('activeMatch', item),
            selected: params.activeMatch,
            emptyText: 'Матчей нет. Сначала выберите тур или дату.',
            searchable: true,
        },
        matches: {
            title: 'Выберите матчи',
            load: loadMatches,
            view: matchView,
            multiple: true,
            onPick: (items) => setValue('activeMatches', items && items.length ? items : false),
            selected: Array.isArray(params.activeMatches) ? params.activeMatches : [],
            emptyText: 'Матчей нет. Сначала выберите тур или дату.',
            searchable: true,
        },
        player: {
            title: 'Выберите игрока',
            load: loadPlayers,
            view: playerView,
            multiple: false,
            onPick: (item) => setValue('activePlayer', {
                ...item,
                id: item.player_id || item.id,
            }),
            selected: params.activePlayer,
            emptyText: 'Игроков нет. Сначала выберите лигу и сезон.',
            searchable: true,
        },
        mainTeam: {
            title: 'Состав первой команды',
            load: () => loadPlayers(homeTeamId),
            view: playerView,
            multiple: true,
            onPick: (items) => setValue('activeMainTeam', items && items.length ? items : false),
            selected: Array.isArray(params.activeMainTeam) ? params.activeMainTeam : [],
            emptyText: 'Игроков нет. Сначала выберите матч.',
            searchable: true,
        },
        subTeam: {
            title: 'Состав второй команды',
            load: () => loadPlayers(guestTeamId),
            view: playerView,
            multiple: true,
            onPick: (items) => setValue('activeSubTeam', items && items.length ? items : false),
            selected: Array.isArray(params.activeSubTeam) ? params.activeSubTeam : [],
            emptyText: 'Игроков нет. Сначала выберите матч.',
            searchable: true,
        },
        coaches: {
            title: 'Тренеры',
            load: () => loadPlayers(homeTeamId),
            view: playerView,
            multiple: true,
            onPick: (items) => setValue('activeCoaches', items && items.length ? items : false),
            selected: Array.isArray(params.activeCoaches) ? params.activeCoaches : [],
            emptyText: 'Списка нет. Сначала выберите матч.',
            searchable: true,
        },
        slotPlayer: {
            title: 'Выберите игрока',
            load: () => loadPlayers(),
            view: playerView,
            multiple: false,
            onPick: (item) => { if (slotRequest) slotRequest.onPick(item); },
            selected: null,
            emptyText: 'Игроков нет. Сначала выберите лигу и сезон.',
            searchable: true,
        },
        slotMatch: {
            title: 'Выберите матч',
            load: loadMatches,
            view: matchView,
            multiple: false,
            onPick: (item) => { if (slotRequest) slotRequest.onPick(item); },
            selected: null,
            emptyText: 'Матчей нет. Сначала выберите тур или дату.',
            searchable: true,
        },
        group: {
            title: 'Выберите группу',
            load: loadGroups,
            view: groupView,
            multiple: false,
            onPick: (item) => setValue('activeGroups', item),
            selected: params.activeGroups,
            emptyText: 'Групп нет.',
        },
        groups: {
            title: 'Выберите группы',
            load: loadGroups,
            view: groupView,
            multiple: true,
            onPick: (items) => setValue('activeGroups', items && items.length ? items : false),
            selected: Array.isArray(params.activeGroups) ? params.activeGroups : [],
            emptyText: 'Групп нет.',
        },
    };

    const activePicker = picker ? PICKERS[picker] : null;

    // ---- Чего не хватает для сохранения ----
    const missing = useMemo(() => {
        if (!pattern) return [];
        return pattern.required_settings
            .filter(key => params[key] === false || params[key] === undefined)
            .map(key => REQUIRED_LABELS[key] || key);
    }, [pattern, params]);

    // Незаполненные параметры шаблона — даже те, что сервер не считает
    // обязательными. Без них он иногда не может собрать картинку
    const emptyControls = useMemo(() => {
        if (!pattern) return [];
        const seen = [];
        pattern.available_settings.forEach(name => {
            const key = CONTROL_STATE_KEYS[name];
            if (!key) return;
            const value = params[key];
            const isEmpty = value === false || value === undefined || value === ''
                || (Array.isArray(value) && value.length === 0);
            const label = REQUIRED_LABELS[key] || key;
            if (isEmpty && seen.indexOf(label) === -1) seen.push(label);
        });
        return seen;
    }, [pattern, params]);

    const canDownload = missing.length === 0
        && !previewLoading
        && (preview.html || preview.base64);

    // Точная сверка по требованию: один серверный рендер PNG вместо рендера
    // на каждое изменение параметра. Быстрое HTML-превью в браузере рисуется
    // не пиксель в пиксель — шрифты и метрики текста отличаются
    const renderExact = async () => {
        if (!pattern || !requestUrl) return;
        setPreviewLoading(true);
        setError(null);
        try {
            const data = await dispatch(fetchPictureBase64(requestUrl, buildRequestParams()));
            if (!data || typeof data !== 'string') {
                throw new Error('Сервер не вернул картинку');
            }
            setPreview({ html: null, base64: data });
            setExact(true);
        } catch (e) {
            console.error('Ошибка точного превью:', e);
            setError(e);
        } finally {
            setPreviewLoading(false);
        }
    };

    const download = async (newsData) => {
        if (!pattern || !requestUrl) return;
        setDownloading(true);
        setError(null);
        try {
            // Если админ уже нажал «Точное превью», PNG на руках — второй
            // тяжёлый рендер не заказываем. С новостью так нельзя: newsData
            // обрабатывается там же, где рисуется картинка
            const data = exact && preview.base64 && !newsData
                ? preview.base64
                : await dispatch(fetchPictureBase64(
                    requestUrl,
                    buildRequestParams(newsData ? { newsData } : undefined),
                ));
            if (!data || typeof data !== 'string') {
                throw new Error('Сервер не вернул картинку');
            }
            downloadBase64Png(data, buildFileName(pattern.title));
        } catch (e) {
            console.error('Ошибка сохранения картинки:', e);
            setError(e);
        } finally {
            setDownloading(false);
            setNewsOpen(false);
        }
    };

    // Шаблоны с новостью сначала спрашивают комментарий, остальные скачиваются сразу
    const startDownload = () => {
        if (patternPostsNews(pattern.id)) {
            setNewsOpen(true);
            return;
        }
        download();
    };

    const publishAndDownload = (comment) => {
        const newsData = buildNewsData(pattern.id, comment, {
            player: params.activePlayer,
            players: params.activePlayers,
            stage: params.activeStage,
            league: params.activeLeague,
            eventValue: SOCIAL_EVENT_TYPES[params.eventIndex]
                ? SOCIAL_EVENT_TYPES[params.eventIndex].value : undefined,
        });
        download(newsData);
    };

    // ---- Рендер ----
    if (patternLoading) {
        return (
            <div className="layoutWrapper">
                <div className="matchCenterWrapper">
                    <div className="igLoader">
                        <Loader type="Oval" color="#04a9f5" height={64} width={64} />
                        <p>Загрузка шаблона…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!pattern) {
        return (
            <div className="layoutWrapper">
                <div className="matchCenterWrapper">
                    <div className="igPage">
                        <div className="igError">Шаблон не найден или недоступен.</div>
                        <button type="button" className="igGhostBtn" onClick={() => history.push('/infographics')}>
                            <FiArrowLeft /> К шаблонам
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const aspect = previewAspectRatio(pattern);
    const naturalWidth = Number(pattern.cut_size_width) > 0
        ? Number(pattern.cut_size_width) : NATURAL_FALLBACK_WIDTH;
    const naturalHeight = Number(pattern.cut_size_height) > 0
        ? Number(pattern.cut_size_height) : NATURAL_FALLBACK_HEIGHT;

    const renderControls = () => pattern.available_settings.map((name, index) => {
        const Control = CONTROL_REGISTRY[name];
        if (Control) {
            return (
                <Control
                    key={`${name}_${index}`}
                    value={params}
                    setValue={setValue}
                    pattern={pattern}
                    ctx={controlContext}
                />
            );
        }
        const label = PENDING_CONTROL_LABELS[name];
        return (
            <div className="igControl igControlPending" key={`${name}_${index}`}>
                <FiAlertCircle />
                <span>
                    {label || name} — этот параметр пока настраивается только в приложении.
                </span>
            </div>
        );
    });

    return (
        <div className="layoutWrapper">
            <div className="matchCenterWrapper">
                <div className="matchCenterHeader">
                    <button type="button" className="backButton" onClick={() => history.push('/infographics')}>
                        <FiArrowLeft /> К шаблонам
                    </button>
                    <span className="matchCenterName">{pattern.title}</span>
                </div>

                <div className="igEditor">
                    <div className="igPreviewColumn">
                        <div
                            className="igPreviewFrame"
                            ref={previewRef}
                            style={{ aspectRatio: String(aspect) }}
                        >
                            {preview.html ? (
                                <iframe
                                    title="Превью"
                                    className="igPreviewFrameInner"
                                    srcDoc={preview.html}
                                    sandbox={PREVIEW_SANDBOX}
                                    scrolling="no"
                                    style={{
                                        width: naturalWidth,
                                        height: naturalHeight,
                                        transform: `scale(${previewScale})`,
                                    }}
                                />
                            ) : preview.base64 ? (
                                <img
                                    className="igPreviewContent"
                                    src={`data:image/png;base64,${preview.base64}`}
                                    alt={pattern.title}
                                />
                            ) : (
                                <div className="igPreviewEmpty">Настройте параметры справа</div>
                            )}

                            {previewLoading && (
                                <div className="igPreviewOverlay">
                                    <Loader type="Oval" color="#04a9f5" height={44} width={44} />
                                </div>
                            )}
                        </div>

                        {error ? (
                            <div className="igError igPreviewError">
                                {describeError(error, emptyControls)}
                            </div>
                        ) : null}

                        <div className="igPreviewActions">
                            <button
                                type="button"
                                className="igPrimaryBtn"
                                disabled={!canDownload || downloading}
                                onClick={startDownload}
                            >
                                <FiDownload />
                                {downloading ? 'Готовим картинку…' : 'Скачать PNG'}
                            </button>

                            <button
                                type="button"
                                className="igGhostBtn"
                                disabled={!canDownload || previewLoading || exact}
                                onClick={renderExact}
                            >
                                <FiEye />
                                {exact ? 'Это точная картинка' : 'Точное превью'}
                            </button>

                            {missing.length > 0 ? (
                                <span className="igMissingHint">
                                    Чтобы скачать, выберите {missing.join(', ')}
                                </span>
                            ) : null}
                        </div>

                        {missing.length === 0 && !exact ? (
                            <p className="igPreviewNote">
                                Превью рисует браузер, поэтому шрифты и отступы могут
                                чуть отличаться от итога. Нажмите «Точное превью»,
                                чтобы посмотреть картинку, отрисованную сервером —
                                она же и скачается.
                            </p>
                        ) : null}
                    </div>

                    <div className="igParamsColumn">
                        <h3 className="igParamsTitle">Параметры</h3>
                        {renderControls()}
                    </div>
                </div>
            </div>

            <NewsRequestModal
                open={newsOpen}
                busy={downloading}
                title={buildNewsData(pattern.id, '', {
                    player: params.activePlayer,
                    players: params.activePlayers,
                    stage: params.activeStage,
                    league: params.activeLeague,
                    eventValue: SOCIAL_EVENT_TYPES[params.eventIndex]
                        ? SOCIAL_EVENT_TYPES[params.eventIndex].value : undefined,
                }).title}
                onSubmit={publishAndDownload}
                onSkip={() => download()}
                onClose={() => setNewsOpen(false)}
            />

            <TourTeamEditor
                open={tourTeamOpen}
                scheme={params.activeScheme}
                players={params.activePlayers}
                onPick={(list) => setValue('activePlayers', list && list.length ? list : false)}
                onClose={() => setTourTeamOpen(false)}
                onOpenPlayerPicker={(banIds, onPickPlayer) => {
                    setSlotRequest({ onPick: onPickPlayer });
                    setPicker('slotPlayer');
                }}
            />

            <PlayoffEditor
                open={playoffOpen}
                rounds={params.activePlayoffMatches}
                onPick={(list) => setValue('activePlayoffMatches', list)}
                onClose={() => setPlayoffOpen(false)}
                onOpenMatchPicker={(onPickMatch) => {
                    setSlotRequest({ onPick: onPickMatch });
                    setPicker('slotMatch');
                }}
            />

            {activePicker && (
                <EntityPickerModal
                    open
                    title={activePicker.title}
                    loadItems={activePicker.load}
                    renderItem={activePicker.view}
                    multiple={activePicker.multiple}
                    selected={activePicker.selected}
                    searchable={activePicker.searchable}
                    emptyText={activePicker.emptyText}
                    onPick={activePicker.onPick}
                    onClose={() => { setPicker(null); setSlotRequest(null); }}
                />
            )}

            {leaguePickerOpen && (
                <div className="modalOverlay" onClick={() => setLeaguePickerOpen(false)}>
                    <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>Выберите лигу</h2>
                            <button className="modalClose" onClick={() => setLeaguePickerOpen(false)}>×</button>
                        </div>
                        <div className="modalBody modalBodyScroll">
                            {leagues.length === 0 ? (
                                <p className="igControlNote">Лиги не найдены.</p>
                            ) : leagues.map(league => (
                                <button
                                    type="button"
                                    key={league.league_id}
                                    className="igListRow"
                                    onClick={() => pickLeague(league)}
                                >
                                    {resolveImage(league.league_image) ? (
                                        <img src={resolveImage(league.league_image)} alt="" />
                                    ) : null}
                                    <span className="igListRowMain">
                                        <span className="igListRowTitle">{league.league_name}</span>
                                        <span className="igListRowMeta">{league.championat_name}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {stagePickerOpen && (
                <div className="modalOverlay" onClick={() => setStagePickerOpen(false)}>
                    <div className="modalContent" onClick={(e) => e.stopPropagation()}>
                        <div className="modalHeader">
                            <h2>Выберите тур</h2>
                            <button className="modalClose" onClick={() => setStagePickerOpen(false)}>×</button>
                        </div>
                        <div className="modalBody modalBodyScroll">
                            {stages.length === 0 ? (
                                <p className="igControlNote">
                                    Туров нет. Сначала выберите лигу и сезон.
                                </p>
                            ) : stages.map(stage => (
                                <button
                                    type="button"
                                    key={stage.id}
                                    className="igListRow"
                                    onClick={() => {
                                        setValue('activeStage', stage);
                                        setStagePickerOpen(false);
                                    }}
                                >
                                    <span className="igListRowMain">
                                        <span className="igListRowTitle">{stage.name}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
